/**
 * Local Delivery Options
 * Handles delivery method selection, date/time scheduling, and cart attribute updates
 */

class LocalDeliveryOptions extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('.local-delivery-options__toggle');
    this.body = this.querySelector('.local-delivery-options__body');
    this.methodInputs = this.querySelectorAll('input[name="delivery_method"]');
    this.dateSelect = this.querySelector('#delivery_date');
    this.timeSelect = this.querySelector('#delivery_time');
    this.storeSelect = this.querySelector('#pickup_store');
    this.storeField = this.querySelector('.local-delivery-options__store-field');
    this.zipDisplay = this.querySelector('.local-delivery-options__zip');
    this.zipInner = this.querySelector('.local-delivery-options__zip-inner');
    this.summaryMethod = this.querySelector('.local-delivery-options__summary-method');
    this.summaryDetails = this.querySelector('.local-delivery-options__summary-details');
    this.badgeMethod = this.querySelector('.local-delivery-options__summary-method-badge');
    this.deliveryDays = parseInt(this.dataset.deliveryDays) || 7;
  }

  connectedCallback() {
    this.populateDates();
    this.bindEvents();
    this.checkVisibility();
    this.updateSummary();

    // Listen for ZIP code updates
    document.addEventListener('zipcode-updated', () => this.checkVisibility());
    document.addEventListener('local-products-filtered', () => this.checkVisibility());
  }

  bindEvents() {
    // Collapsible toggle
    this.toggle?.addEventListener('click', () => this.toggleBody());

    // Delivery method change
    this.methodInputs.forEach(input => {
      input.addEventListener('change', () => this.handleMethodChange());
    });

    // Date/time/store change
    this.dateSelect?.addEventListener('change', () => this.updateSummary());
    this.timeSelect?.addEventListener('change', () => this.updateSummary());
    this.storeSelect?.addEventListener('change', () => this.updateSummary());
  }

  toggleBody() {
    if (!this.body || !this.toggle) return;

    const isExpanded = this.toggle.getAttribute('aria-expanded') === 'true';
    this.toggle.setAttribute('aria-expanded', String(!isExpanded));
    this.body.hidden = isExpanded;
  }

  checkVisibility() {
    const isLocal = window.ZipLookup?.isLocalCustomer() || false;
    const storedZip = window.ZipLookup?.getStoredZip() || '';

    if (isLocal && storedZip) {
      this.style.display = 'block';
      this.updateZipDisplay(storedZip);
    } else {
      this.style.display = 'none';
    }
  }

  updateZipDisplay(zip) {
    if (this.zipDisplay) this.zipDisplay.textContent = zip;
    if (this.zipInner) this.zipInner.textContent = zip;
  }

  populateDates() {
    if (!this.dateSelect) return;

    const today = new Date();
    const options = { weekday: 'short', month: 'short', day: 'numeric' };

    // Clear existing options
    this.dateSelect.innerHTML = '';

    for (let i = 0; i < this.deliveryDays; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const option = document.createElement('option');
      option.value = date.toISOString().split('T')[0];

      if (i === 0) {
        option.textContent = 'Today';
      } else if (i === 1) {
        option.textContent = 'Tomorrow';
      } else {
        option.textContent = date.toLocaleDateString('en-US', options);
      }

      this.dateSelect.appendChild(option);
    }
  }

  handleMethodChange() {
    const selectedMethod = this.querySelector('input[name="delivery_method"]:checked')?.value;

    // Show/hide store field based on method
    if (this.storeField) {
      this.storeField.style.display = selectedMethod === 'pickup' ? 'block' : 'none';
    }

    this.updateSummary();
  }

  updateSummary() {
    const method = this.querySelector('input[name="delivery_method"]:checked')?.value || 'delivery';
    const date = this.dateSelect?.value || '';
    const time = this.timeSelect?.value || '';
    const store = this.storeSelect?.value || '';

    const methodLabel = method === 'pickup' ? 'Store Pickup' : 'Same-day Delivery';

    // Update summary display inside body
    if (this.summaryMethod) {
      this.summaryMethod.textContent = methodLabel;
    }

    // Update badge on the toggle header
    if (this.badgeMethod) {
      this.badgeMethod.textContent = methodLabel;
    }

    if (this.summaryDetails) {
      let details = '';
      const dateObj = new Date(date + 'T12:00:00');
      const dateStr = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

      if (method === 'pickup' && store) {
        details = `${store} - ${dateStr} at ${time}`;
      } else if (date && time) {
        details = `${dateStr} between ${time}`;
      }

      this.summaryDetails.textContent = details;
    }

    // Update cart attributes
    this.updateCartAttributes({
      method,
      date,
      time,
      store: method === 'pickup' ? store : ''
    });
  }

  async updateCartAttributes(data) {
    const zip = window.ZipLookup?.getStoredZip() || '';

    const attributes = {
      'Delivery Method': data.method === 'pickup' ? 'Store Pickup' : 'Same-day Delivery',
      'Delivery Date': data.date,
      'Time Slot': data.time,
      'Customer ZIP': zip
    };

    if (data.method === 'pickup' && data.store) {
      attributes['Pickup Store'] = data.store;
    }

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes })
      });

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('delivery-options-updated', {
        detail: { ...data, zip }
      }));
    } catch (error) {
      console.error('Failed to update cart attributes:', error);
    }
  }

  // Get current selections
  getSelections() {
    return {
      method: this.querySelector('input[name="delivery_method"]:checked')?.value || 'delivery',
      date: this.dateSelect?.value || '',
      time: this.timeSelect?.value || '',
      store: this.storeSelect?.value || '',
      zip: window.ZipLookup?.getStoredZip() || ''
    };
  }
}

// Register custom element
if (!customElements.get('local-delivery-options')) {
  customElements.define('local-delivery-options', LocalDeliveryOptions);
}

// Export for external access
window.LocalDeliveryOptions = {
  getSelections: () => {
    const component = document.querySelector('local-delivery-options');
    return component?.getSelections() || null;
  }
};